import { useCallback, useMemo, useState } from "react";
import { AlertTriangle, Clock, MessageSquareWarning, Newspaper, Bell, CheckCheck, Check, CheckCircle2 } from "lucide-react";
import { NOTIFICATION_TYPE_LABEL, NUM, CARD, formatWhen } from "../lib/dashboardTokens";
import { useDemoIdentity } from "../lib/DemoIdentityContext";
import { Button } from "../components/ui/button";

// Not linked from nav (see components/Layout.jsx) and no backend source yet — see
// docs/backend-integration.md item 8. Kept as a working, empty inbox shell.
const NOTIFICATIONS = [];

const TYPE_ICON = {
  escalation: AlertTriangle,
  sla_breach: Clock,
  sentiment_pattern: MessageSquareWarning,
  digest: Newspaper,
};

const TYPE_ORDER = ["escalation", "sla_breach", "sentiment_pattern", "digest"];

export default function Notifications() {
  const { scopeLocations } = useDemoIdentity();

  // Scope by the current demo identity: master sees everything, everyone else
  // sees only the workshop(s) currently in view.
  const scoped = useMemo(() => {
    const list =
      scopeLocations === "all" ? NOTIFICATIONS : NOTIFICATIONS.filter((n) => scopeLocations.includes(n.workshopLocation));
    return [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [scopeLocations]);

  // Local "read" state only — there is no backend here. Seeded from the seed data's
  // own readAt field, keyed by notification id so it survives scope/filter changes.
  const [readAt, setReadAt] = useState(() => Object.fromEntries(NOTIFICATIONS.map((n) => [n.id, n.readAt])));

  const [typeFilter, setTypeFilter] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);

  const isUnread = useCallback((n) => !readAt[n.id], [readAt]);

  const typeCounts = useMemo(() => {
    const counts = { all: scoped.length };
    TYPE_ORDER.forEach((t) => {
      counts[t] = 0;
    });
    scoped.forEach((n) => {
      counts[n.type] = (counts[n.type] || 0) + 1;
    });
    return counts;
  }, [scoped]);

  const unreadCount = useMemo(() => scoped.filter(isUnread).length, [scoped, isUnread]);

  const visible = useMemo(
    () => scoped.filter((n) => (typeFilter === "all" || n.type === typeFilter) && (!unreadOnly || isUnread(n))),
    [scoped, typeFilter, unreadOnly, isUnread]
  );

  const visibleUnreadCount = visible.filter(isUnread).length;

  const markRead = (id) => setReadAt((prev) => ({ ...prev, [id]: new Date().toISOString() }));

  const markAllVisibleRead = () => {
    const now = new Date().toISOString();
    setReadAt((prev) => {
      const next = { ...prev };
      visible.forEach((n) => {
        if (!next[n.id]) next[n.id] = now;
      });
      return next;
    });
  };

  const emptyMessage =
    scoped.length === 0
      ? "There's nothing here for the workshops you're viewing."
      : unreadOnly
      ? "Nothing unread right now."
      : "Nothing matches what you've selected.";

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="notifications-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-work text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Notifications</h1>
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 text-xs font-medium text-blue-700"
                data-testid="unread-count-badge"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                <span className={NUM}>{unreadCount}</span> unread
              </span>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={markAllVisibleRead}
          disabled={visibleUnreadCount === 0}
          data-testid="mark-all-read-btn"
        >
          <CheckCheck size={15} /> Mark all as read
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div
          className="flex gap-2 overflow-x-auto scrollbar-none -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap"
          data-testid="type-filter"
        >
          {[{ key: "all", label: "All" }, ...TYPE_ORDER.map((t) => ({ key: t, label: NOTIFICATION_TYPE_LABEL[t] }))].map(
            ({ key, label }) => (
              <button
                key={key}
                data-testid={`type-pill-${key}`}
                onClick={() => setTypeFilter(key)}
                className={`rounded-full px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-colors border whitespace-nowrap shrink-0 ${
                  typeFilter === key
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                }`}
              >
                {label}{" "}
                <span className={`${NUM} ${typeFilter === key ? "text-blue-100" : "text-slate-400"}`}>
                  {typeCounts[key] ?? 0}
                </span>
              </button>
            )
          )}
        </div>

        <button
          data-testid="unread-only-toggle"
          onClick={() => setUnreadOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs sm:text-sm font-medium border whitespace-nowrap transition-colors ${
            unreadOnly ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
          }`}
        >
          {unreadOnly ? "Showing unread only" : "Show unread only"}
        </button>
      </div>

      {visible.length === 0 ? (
        <div className={`${CARD} flex flex-col items-center justify-center text-center py-14`} data-testid="notifications-empty">
          <CheckCircle2 className="text-emerald-500 mb-3" size={32} />
          <h3 className="font-work text-base font-medium text-slate-800">You're all caught up</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-sm">{emptyMessage}</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="notifications-list">
          {visible.map((n) => {
            const Icon = TYPE_ICON[n.type] || Bell;
            const unread = isUnread(n);
            return (
              <li
                key={n.id}
                data-testid={`notification-${n.id}`}
                className={`${CARD} flex items-start gap-3 border-l-4 ${
                  unread ? "border-l-blue-400 bg-blue-50/30" : "border-l-transparent"
                }`}
              >
                <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500">
                  <Icon size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {unread && (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700"
                        data-testid={`unread-flag-${n.id}`}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> Unread
                      </span>
                    )}
                    <span className="text-[11px] uppercase tracking-wide text-slate-400">
                      {NOTIFICATION_TYPE_LABEL[n.type] || n.type}
                    </span>
                    <span className="text-xs text-slate-400">{formatWhen(n.createdAt)}</span>
                    {n.workshopLocation && <span className="text-xs text-slate-400">· {n.workshopLocation}</span>}
                  </div>
                  <div className={`mt-1 text-sm ${unread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`}>
                    {n.title}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{n.body}</p>
                </div>
                <div className="shrink-0">
                  {unread ? (
                    <Button variant="outline" size="sm" onClick={() => markRead(n.id)} data-testid={`mark-read-${n.id}`}>
                      <Check size={14} /> Mark as read
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-400" data-testid={`read-flag-${n.id}`}>
                      Read
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
